/**
 * Clipboard operations utilities
 * Extracted from MyCanvas.js to reduce file size
 */

import { v4 as uuidv4 } from "uuid";
import { isShapeType } from "../../../util/shapeTypeMapper";
import {
  isSupportedImageFormat,
  getUnsupportedFormatMessage,
  validateImageFile,
  validateDataUrlImage,
  validateImageUrl,
  detectImageFormatFromBlob,
  getUnsupportedFormatInfo,
  validateImageBlobContent,
} from "./imageFormatUtils";

const pasteFlowDebug = (...args) => {
  try {
  
  } catch {
    /* noop */
  }
};

/** Pasted images (toolbar / system clipboard) should live on the base layer, not the top layer. */
const CLIPBOARD_PASTE_IMAGE_OPTS = {
  isOnTopLayer: false,
  /** Flattened PNG/WebP may bake in UI checkerboard pixels — strip when detected. */
  stripCheckerboard: true,
};

/**
 * Prefer clipboard image/png (etc.) over HTML &lt;img&gt; URLs when both exist.
 * Preview URLs (e.g. toppng) often serve PNGs with a baked-in checkerboard; the
 * clipboard bitmap goes through stripCheckerboard in addImage.
 */
const PREFERRED_RASTER_CLIPBOARD_MIMES = [
  "image/png",
  "image/webp",
  "image/jpeg",
  "image/jpg",
];

/**
 * @param {ClipboardItem[]} clipboardItems
 * @returns {Promise<boolean>} true if addImage was invoked
 */
async function tryPastePreferredRasterFromClipboardItems(
  clipboardItems,
  addImage,
  showError
) {
  for (const clipboardItem of clipboardItems) {
    const types = clipboardItem.types || [];
    let unsupportedMimeError = null;
    for (const type of types) {
      if (!type.startsWith("image/")) continue;
      const unsupportedInfo = getUnsupportedFormatInfo(type);
      if (unsupportedInfo) {
        unsupportedMimeError = getUnsupportedFormatMessage(type);
        break;
      }
    }
    if (unsupportedMimeError) {
      if (showError) showError(unsupportedMimeError);
      else alert(unsupportedMimeError);
      continue;
    }

    for (const mime of PREFERRED_RASTER_CLIPBOARD_MIMES) {
      if (!types.includes(mime)) continue;
      try {
        const blob = await clipboardItem.getType(mime);
        const validation = await validateImageBlobContent(
          blob,
          `pasted-image-${Date.now()}`
        );
        if (!validation.isValid) {
          continue;
        }
        pasteFlowDebug(
          "Clipboard API: preferred raster blob (runs stripCheckerboard; before HTML URL)",
          { mime, blobSize: blob.size }
        );
        const src = URL.createObjectURL(blob);
        addImage(
          src,
          "",
          blob,
          null,
          null,
          null,
          null,
          false,
          CLIPBOARD_PASTE_IMAGE_OPTS
        );
        return true;
      } catch (blobError) {
        console.error(
          "Preferred raster clipboard read failed:",
          mime,
          blobError
        );
      }
    }
  }
  return false;
}

/**
 * @param {DataTransferItemList} items
 * @returns {Promise<boolean>}
 */
async function tryPastePreferredRasterFromDataTransferItems(
  items,
  addImage,
  showError,
  setIsImageLoading,
  setImageLoadingProgress,
  e
) {
  for (const mime of PREFERRED_RASTER_CLIPBOARD_MIMES) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type !== mime) continue;
      const unsupportedInfo = getUnsupportedFormatInfo(item.type);
      if (unsupportedInfo) {
        const errorMessage = getUnsupportedFormatMessage(item.type);
        if (showError) showError(errorMessage);
        else alert(errorMessage);
        continue;
      }
      const blob = item.getAsFile();
      if (!blob) continue;
      const validation = await validateImageBlobContent(
        blob,
        `pasted-image-${Date.now()}`
      );
      if (!validation.isValid) {
        continue;
      }
      pasteFlowDebug(
        "native paste: preferred raster blob first (stripCheckerboard in addImage)",
        { mime: item.type, blobSize: blob.size }
      );
      e.preventDefault();
      setIsImageLoading(true);
      setImageLoadingProgress(10);
      const src = URL.createObjectURL(blob);
      addImage(
        src,
        "",
        blob,
        null,
        null,
        null,
        null,
        false,
        CLIPBOARD_PASTE_IMAGE_OPTS
      );
      return true;
    }
  }
  return false;
}

/**
 * Parse clipboard HTML for the best http(s) image URL (e.g. Google Images, web pages).
 * Browsers often place a flattened image/png on the clipboard that includes the site's
 * transparency checkerboard behind thumbnails; the original asset URL in HTML usually does not.
 * @param {string} html
 * @returns {{ url: string, score: number } | null}
 */
const parseSrcsetCandidates = (srcset, attrScore, consider) => {
  if (!srcset || typeof srcset !== "string") return;
  for (const part of srcset.split(",")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const mw = trimmed.match(/^(\S+)\s+(\d+)w$/i);
    if (mw) {
      const sw = parseInt(mw[2], 10) || 0;
      consider(mw[1], sw * sw + attrScore);
      continue;
    }
    const mx = trimmed.match(/^(\S+)\s+(\d+(?:\.\d+)?)x$/i);
    if (mx) {
      const mult = parseFloat(mx[2]) || 1;
      consider(mx[1], Math.round(4000 * mult) + attrScore);
      continue;
    }
    const lone = trimmed.split(/\s+/)[0];
    if (lone && (lone.startsWith("http://") || lone.startsWith("https://"))) {
      consider(lone, (attrScore || 0) + 800);
    }
  }
};

const extractBestHttpImageCandidateFromHtmlFragment = (html) => {
  if (!html || typeof html !== "string") return null;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const imgs = doc.querySelectorAll("img[src], img[srcset]");
    const pictureSources = doc.querySelectorAll("picture source[srcset]");
    let bestUrl = null;
    let bestScore = -1;

    const consider = (url, score) => {
      if (!url || typeof url !== "string") return;
      const u = url.trim();
      if (!u.startsWith("http://") && !u.startsWith("https://")) return;
      if (/favicon/i.test(u)) return;
      if (score > bestScore) {
        bestScore = score;
        bestUrl = u;
      }
    };

    const scoreHttpSrc = (src, attrScore) => {
      if (!src || (!src.startsWith("http://") && !src.startsWith("https://")))
        return;
      let score = attrScore;
      if (!score) {
        if (/\.(png|webp|gif)(\?|#|$)/i.test(src)) score = 5000;
        else if (
          /encrypted-tbn|googleusercontent|gstatic|ggpht|googleapis/i.test(src)
        )
          score = 3000;
        else score = 1000;
      }
      consider(src, score);
    };

    for (const el of imgs) {
      const w = parseInt(el.getAttribute("width"), 10) || 0;
      const h = parseInt(el.getAttribute("height"), 10) || 0;
      const attrScore = w > 0 && h > 0 ? w * h : 0;

      scoreHttpSrc(el.getAttribute("src")?.trim(), attrScore);
      parseSrcsetCandidates(el.getAttribute("srcset"), attrScore, consider);
    }

    for (const el of pictureSources) {
      parseSrcsetCandidates(el.getAttribute("srcset"), 0, consider);
    }

    return bestUrl ? { url: bestUrl, score: bestScore } : null;
  } catch {
    return null;
  }
};

/**
 * @param {string} html
 * @returns {string | null}
 */
export const extractBestHttpImageUrlFromClipboardHtml = (html) =>
  extractBestHttpImageCandidateFromHtmlFragment(html)?.url ?? null;

/**
 * Merge candidates from multiple HTML fragments (e.g. several Clipboard API items).
 * @param {string[]} htmlFragments
 * @returns {string | null}
 */
export const extractBestHttpImageUrlFromClipboardHtmlFragments = (
  htmlFragments
) => {
  if (!Array.isArray(htmlFragments) || htmlFragments.length === 0) return null;
  let bestUrl = null;
  let bestScore = -1;
  for (const html of htmlFragments) {
    const c = extractBestHttpImageCandidateFromHtmlFragment(html);
    if (c && c.score > bestScore) {
      bestScore = c.score;
      bestUrl = c.url;
    }
  }
  return bestUrl;
};

/**
 * Sanitize an object for cloning by removing function-type properties
 * @param {Object} obj - Object to sanitize
 * @returns {Object} Sanitized object clone
 */
export const sanitizeForClone = (obj) => {
  const clone = { ...obj };

  // Remove any function-type properties
  for (const key in clone) {
    if (typeof clone[key] === "function") {
      delete clone[key];
    }

    // Optionally, deep-sanitize children if they exist
    if (Array.isArray(clone[key])) {
      clone[key] = clone[key].map((item) =>
        typeof item === "object" && item !== null
          ? sanitizeForClone(item)
          : item
      );
    }
  }

  return clone;
};

/**
 * Create a function to handle copying selected objects to clipboard
 * @param {Function} getSelectedObjects - Function to get current selected objects
 * @param {Function} setClipboard - Setter for clipboard state
 * @returns {Function} Function to handle copy operation
 */
export const createHandleCopy = (getSelectedObjects, setClipboard) => () => {
  const selectedObjects = getSelectedObjects();
  if (selectedObjects.length === 0) return;

  const copied = selectedObjects.map((obj) => ({
    ...sanitizeForClone(obj),
    id: `copy_${Date.now()}_${obj.id}`,
  }));

  setClipboard(copied);
};

/**
 * Create a function to handle cutting selected objects from canvas
 * @param {Function} getSelectedObjects - Function to get current selected objects
 * @param {Function} handleCopy - Function to copy objects to clipboard
 * @param {Function} setImages - Setter for images array
 * @param {Function} setRectangles - Setter for rectangles array
 * @param {Function} setCircles - Setter for circles array
 * @param {Function} setLines - Setter for lines array
 * @param {Function} setArrows - Setter for arrows array
 * @param {Function} setDoubleSidedArrows - Setter for double-sided arrows array
 * @param {Function} setTexts - Setter for texts array
 * @param {Function} setSelectedObjects - Setter for selected objects array
 * @param {Function} setSelectedObject - Setter for selected object
 * @param {Function} saveToHistory - Function to save to history
 * @returns {Function} Function to handle cut operation
 */
export const createHandleCut =
  (
    getSelectedObjects,
    handleCopy,
    setImages,
    setRectangles,
    setCircles,
    setLines,
    setArrows,
    setDoubleSidedArrows,
    setTexts,
    setSelectedObjects,
    setSelectedObject,
    saveToHistory
  ) =>
  () => {
    const selectedObjects = getSelectedObjects();
    if (selectedObjects.length === 0) return;

    // Save a copy
    handleCopy();

    // Remove objects from canvas
    const idsToCut = new Set(selectedObjects.map((obj) => obj.id));

    setImages((prev) => prev.filter((img) => !idsToCut.has(img.id)));
    setRectangles((prev) => prev.filter((rect) => !idsToCut.has(rect.id)));
    setCircles((prev) => prev.filter((c) => !idsToCut.has(c.id)));
    setLines((prev) => prev.filter((line) => !idsToCut.has(line.id)));
    setArrows((prev) => prev.filter((arrow) => !idsToCut.has(arrow.id)));
    setDoubleSidedArrows((prev) =>
      prev.filter((arrow) => !idsToCut.has(arrow.id))
    );
    setTexts((prev) => prev.filter((txt) => !idsToCut.has(txt.id)));

    // Clear selection
    setSelectedObjects([]);
    setSelectedObject(null);

    // Save to history after cutting elements
    saveToHistory();
  };

/**
 * Sanitize elements for storage by converting HTMLImageElement to data URLs
 * @param {Array} elements - Array of elements to sanitize
 * @returns {Array} Array of sanitized elements
 */
export const sanitizeElements = (elements) =>
  elements.map((el) => {
    if (
      el.image instanceof HTMLImageElement ||
      el.originalImage instanceof Image
    ) {
      // Convert image to base64 data URL
      const canvas = document.createElement("canvas");
      canvas.width = el.image.naturalWidth;
      canvas.height = el.image.naturalHeight;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(el.image, 0, 0);

      const dataUrl = canvas.toDataURL();

      return {
        ...el,
        imageDataUrl: dataUrl, // Save as string
        image: undefined, // Remove the HTMLImageElement
        originalImage: undefined,
      };
    }

    return el;
  });

/**
 * Create a function to copy the selected image
 * @param {Object} params - Parameters for copying image
 * @param {Object} params.selectedObject - Currently selected object
 * @param {Array} params.images - Current images array
 * @param {Function} params.generateMarkerId - Function to generate marker IDs
 * @param {Function} params.setImages - Setter for images array
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.addImage - Function to add an image
 * @param {Function} params.saveToHistory - Function to save to history
 * @returns {Function} Function to copy selected image
 */
export const createCopySelectedImage = ({
  selectedObject,
  images,
  offCanvasImages = [],
  generateMarkerId,
  setImages,
  setOffCanvasImages = null,
  setSelectedObject,
  addImage,
  saveToHistory,
}) => {
  return () => {
    if (!selectedObject || selectedObject.type !== "Image") return;
    const isOffCanvasImage =
      selectedObject?._isInOffCanvasDuringDrag === true ||
      offCanvasImages.some((img) => img.id === selectedObject.id);
    const targetImages = isOffCanvasImage ? offCanvasImages : images;
    const setTargetImages =
      isOffCanvasImage && typeof setOffCanvasImages === "function"
        ? setOffCanvasImages
        : setImages;

    // If the image is cropped, preserve its dimensions and properties
    if (selectedObject.cropData) {
      // Create a new image object with the same properties as the selected image
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = selectedObject.src;

      img.onload = () => {
        const imageNumber = targetImages.length + 1;
        const newImage = {
          id: generateMarkerId(),
          image: img,
          x: selectedObject.x + 20, // Offset slightly to avoid overlap
          y: selectedObject.y + 20,
          width: selectedObject.width, // Preserve the display dimensions
          height: selectedObject.height, // Preserve the display dimensions
          src: selectedObject.src,
          maintainAspectRatio: selectedObject.maintainAspectRatio,
          compress: selectedObject.compress,
          originalImage: selectedObject.originalImage, // Preserve original image
          compressedImage: selectedObject.compressedImage,
          imageNumber,
          name: `Picture ${imageNumber}`,
          referenceUrl: selectedObject.referenceUrl,
          // Preserve crop data and original image data
          originalImageDataUrl: selectedObject.originalImageDataUrl,
          imageDataUrl: selectedObject.imageDataUrl,
          cropData: selectedObject.cropData, // Preserve crop data
          rotation: selectedObject.rotation || 0,
          scaleX: selectedObject.scaleX || 1,
          scaleY: selectedObject.scaleY || 1,
          // CRITICAL: Preserve crop-related transformation properties
          flipHorizontal: selectedObject.flipHorizontal || false,
          flipVertical: selectedObject.flipVertical || false,
          originalCropRotation: selectedObject.originalCropRotation,
          hasBakedTransforms: selectedObject.hasBakedTransforms || false,
          originalWidth: selectedObject.originalWidth,
          originalHeight: selectedObject.originalHeight,
          originalAspectRatio: selectedObject.originalAspectRatio,
          uncompressedCroppedImage: selectedObject.uncompressedCroppedImage,
          uncompressedCroppedImageUrl:
            selectedObject.uncompressedCroppedImageUrl,
          isOnTopLayer: selectedObject.isOnTopLayer || false,
        };

        setTargetImages((prevImages) => {
          const updatedImages = [...prevImages, newImage];

          // Automatically select the newly created image
          setSelectedObject({
            ...newImage,
            type: "Image",
          });

          // Save to history
          setTimeout(() => {
            saveToHistory("copy_cropped_image", updatedImages);
          }, 100);

          return updatedImages;
        });
      };
    } else {
      // For non-cropped images, preserve all properties including rotation
      // Use existing image element if available, otherwise load it
      const useExistingImage =
        selectedObject.image &&
        selectedObject.image instanceof HTMLImageElement;

      const processCopy = (imgElement) => {
        const imageNumber = targetImages.length + 1;
        const newImage = {
          id: generateMarkerId(),
          image: imgElement,
          x: selectedObject.x + 20, // Offset slightly to avoid overlap
          y: selectedObject.y + 20,
          width: selectedObject.width, // Preserve the display dimensions
          height: selectedObject.height, // Preserve the display dimensions
          src: selectedObject.src,
          maintainAspectRatio: selectedObject.maintainAspectRatio,
          compress: selectedObject.compress,
          originalImage: selectedObject.originalImage, // Preserve original image
          compressedImage: selectedObject.compressedImage,
          imageNumber,
          name: `Picture ${imageNumber}`,
          referenceUrl: selectedObject.referenceUrl,
          // Preserve image data URLs
          originalImageDataUrl: selectedObject.originalImageDataUrl,
          imageDataUrl: selectedObject.imageDataUrl,
          // Preserve rotation and transform properties
          rotation: selectedObject.rotation || 0,
          scaleX: selectedObject.scaleX || 1,
          scaleY: selectedObject.scaleY || 1,
          // Preserve flip properties
          flipHorizontal: selectedObject.flipHorizontal || false,
          flipVertical: selectedObject.flipVertical || false,
          // Preserve dimension properties
          originalWidth: selectedObject.originalWidth,
          originalHeight: selectedObject.originalHeight,
          originalAspectRatio: selectedObject.originalAspectRatio,
          // Preserve layer property
          isOnTopLayer: selectedObject.isOnTopLayer || false,
        };

        setTargetImages((prevImages) => {
          const updatedImages = [...prevImages, newImage];

          // Automatically select the newly created image
          setSelectedObject({
            ...newImage,
            type: "Image",
          });

          // Save to history
          setTimeout(() => {
            saveToHistory("copy_image", updatedImages);
          }, 100);

          return updatedImages;
        });
      };

      if (useExistingImage) {
        // Use existing image element directly
        processCopy(selectedObject.image);
      } else {
        // Load the image first
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = selectedObject.src;

        img.onload = () => {
          processCopy(img);
        };

        img.onerror = () => {
          // Fallback to addImage if image fails to load
          addImage(selectedObject.src);
        };
      }
    }
  };
};

/**
 * Create a function to cut the selected image
 * @param {Object} params - Parameters for cutting image
 * @param {Object} params.selectedObject - Currently selected object
 * @param {Array} params.images - Current images array (needed to save state before removal)
 * @param {Function} params.setClipboard - Setter for clipboard state
 * @param {Function} params.setImages - Setter for images array
 * @param {Function} params.setSelectedImage - Setter for selected image
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.saveToHistory - Function to save to history
 * @returns {Function} Function to cut selected image
 */
export const createCutSelectedImage = ({
  selectedObject,
  images,
  offCanvasImages = [],
  setClipboard,
  setImages,
  setOffCanvasImages = null,
  setSelectedImage,
  setSelectedObject,
  saveToHistory,
}) => {
  return () => {
    if (!selectedObject || selectedObject.type !== "Image") return;
    const isOffCanvasImage =
      selectedObject?._isInOffCanvasDuringDrag === true ||
      offCanvasImages.some((img) => img.id === selectedObject.id);
    const sourceImages = isOffCanvasImage ? offCanvasImages : images;
    const setSourceImages =
      isOffCanvasImage && typeof setOffCanvasImages === "function"
        ? setOffCanvasImages
        : setImages;

    // Store in internal clipboard for pasting
    const imageToStore = {
      ...sanitizeForClone(selectedObject),
      id: `cut_${Date.now()}_${selectedObject.id}`,
    };

    setClipboard([imageToStore]);

    // CRITICAL: Update the image in the images array with the latest data from selectedObject
    // This ensures cropped data (cropData, imageDataUrl, etc.) is properly saved to history
    // The selectedObject may have more up-to-date data than what's in the images array
    const updatedImages = sourceImages.map((img) => {
      if (img.id === selectedObject.id) {
        // CRITICAL: Extract crop-related properties from selectedObject before merging
        // This ensures they're preserved even if selectedObject has other properties that might interfere
        const cropData =
          selectedObject.cropData !== undefined &&
          selectedObject.cropData !== null
            ? selectedObject.cropData
            : img.cropData;
        const imageDataUrl = selectedObject.imageDataUrl || img.imageDataUrl;
        const originalImageDataUrl =
          selectedObject.originalImageDataUrl || img.originalImageDataUrl;
        const originalCropRotation =
          selectedObject.originalCropRotation !== undefined
            ? selectedObject.originalCropRotation
            : img.originalCropRotation;
        const flipHorizontal =
          selectedObject.flipHorizontal !== undefined
            ? selectedObject.flipHorizontal
            : img.flipHorizontal;
        const flipVertical =
          selectedObject.flipVertical !== undefined
            ? selectedObject.flipVertical
            : img.flipVertical;
        const hasBakedTransforms =
          selectedObject.hasBakedTransforms !== undefined
            ? selectedObject.hasBakedTransforms
            : img.hasBakedTransforms;

        // Merge selectedObject into img, then explicitly set crop-related properties
        const updatedImg = {
          ...img,
          ...selectedObject,
          // CRITICAL: Explicitly set crop-related properties AFTER merging to ensure they're preserved
          cropData,
          imageDataUrl,
          originalImageDataUrl,
          originalCropRotation,
          flipHorizontal,
          flipVertical,
          hasBakedTransforms,
          // Preserve image objects if they exist (don't overwrite with undefined)
          image: img.image || selectedObject.image,
          originalImage: img.originalImage || selectedObject.originalImage,
          compressedImage:
            img.compressedImage || selectedObject.compressedImage,
        };

        // Debug logging to verify cropped data is being preserved

        return updatedImg;
      }
      return img;
    });

    // CRITICAL: Save to history BEFORE removing the image
    // This ensures the history entry contains the image with all its cropped data
    // Pass the updated images array (with the image still in it and with latest cropped data) as override
    // saveToHistory accepts positional args: (operationType, currentImagesOverride, ...)
    if (saveToHistory && updatedImages) {
      if (isOffCanvasImage) {
        saveToHistory(
          "cut_image",
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          updatedImages,
        );
      } else {
        saveToHistory("cut_image", updatedImages);
      }
    }

    // Remove the image from canvas
    setSourceImages((prev) =>
      prev.filter((img) => img.id !== selectedObject.id),
    );

    setSelectedImage(null);
    setSelectedObject(null);
  };
};

/**
 * Create a function to handle pasting elements from clipboard
 * @param {Object} params - Parameters for pasting elements
 * @param {Array} params.clipboard - Current clipboard array
 * @param {Function} params.setImages - Setter for images array
 * @param {Function} params.setRectangles - Setter for rectangles array
 * @param {Function} params.setCircles - Setter for circles array
 * @param {Function} params.setArrows - Setter for arrows array
 * @param {Function} params.setDoubleSidedArrows - Setter for double-sided arrows array
 * @param {Function} params.setLines - Setter for lines array
 * @param {Function} params.setTexts - Setter for texts array
 * @param {Function} params.setSelectedObjects - Setter for selected objects array
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.saveToHistory - Function to save to history
 * @returns {Function} Function to paste elements
 */
export const createHandlePasteElements = ({
  clipboard,
  setImages,
  setRectangles,
  setCircles,
  setArrows,
  setDoubleSidedArrows,
  setLines,
  setTexts,
  setSelectedObjects,
  setSelectedObject,
  saveToHistory,
}) => {
  return () => {
    if (clipboard.length === 0) return;

    const getImageDimensionsWithOriginalAspectRatio = (item) => {
      const ratio = Number(item.originalAspectRatio);
      if (!Number.isFinite(ratio) || ratio <= 0) {
        return { width: item.width, height: item.height };
      }

      const width = Number(item.width);
      const height = Number(item.height);

      // Preserve current visual scale, but restore original aspect ratio.
      if (Number.isFinite(width) && width > 0) {
        return { width, height: width / ratio };
      }
      if (Number.isFinite(height) && height > 0) {
        return { width: height * ratio, height };
      }

      return {
        width: item.originalWidth || item.width,
        height: item.originalHeight || item.height,
      };
    };

    // Custom clone function that handles HTMLImageElement objects
    const cloneItem = (item) => {
      if (isShapeType(item.type, "Image")) {
        const adjustedDimensions = getImageDimensionsWithOriginalAspectRatio(item);
        // For images, we need to handle HTMLImageElement objects specially
        // They cannot be cloned, so we just copy the reference
        return {
          ...item,
          ...adjustedDimensions,
          // Preserve HTMLImageElement references (don't clone them)
          image: item.image,
          originalImage: item.originalImage,
          compressedImage: item.compressedImage,
          uncompressedCroppedImage: item.uncompressedCroppedImage,
        };
      } else {
        // For other elements, use structuredClone
        return structuredClone(item);
      }
    };

    const newItems = clipboard.map((item) => ({
      ...cloneItem(item),
      id: `marker-${Date.now()}-${uuidv4()}`,
      x: item.x + 20,
      y: item.y + 20,
      ...(item.type === "Text" || isShapeType(item.type, "Text")
        ? { createdOnCanvas: true }
        : {}),
    }));

    newItems.forEach((item) => {
      if (isShapeType(item.type, "Image")) setImages((prev) => [...prev, item]);
      else if (isShapeType(item.type, "Rect"))
        setRectangles((prev) => [...prev, item]);
      else if (isShapeType(item.type, "Circle"))
        setCircles((prev) => [...prev, item]);
      else if (isShapeType(item.type, "Arrow"))
        setArrows((prev) => [...prev, item]);
      else if (isShapeType(item.type, "DoubleSidedArrow"))
        setDoubleSidedArrows((prev) => [...prev, item]);
      else if (isShapeType(item.type, "Line"))
        setLines((prev) => [...prev, item]);
      else if (isShapeType(item.type, "Text"))
        setTexts((prev) => [...prev, item]);
    });

    setSelectedObjects(newItems);
    setSelectedObject(newItems[0] || null);

    // Save to history after pasting elements
    saveToHistory();
  };
};

/**
 * Utility function to check if an element is a text input or contentEditable
 * @param {HTMLElement} element - Element to check
 * @returns {boolean} Whether element is a text input or contentEditable
 */
export const isTextInputOrContentEditable = (element) => {
  while (element) {
    if (
      element.tagName === "INPUT" ||
      element.tagName === "TEXTAREA" ||
      element.isContentEditable
    ) {
      return true;
    }
    element = element.parentElement;
  }
  return false;
};

/**
 * Toolbar, zoom bar, canvas height editor, shape editor, and other chrome marked with
 * data-canvas-chrome or known wrapper classes (Escape blurs focus and dispatches canvasEscape).
 */
export const isInsideCanvasChrome = (element) => {
  if (!element || typeof element.closest !== "function") return false;
  return !!element.closest(
    [
      ".main-toolbar-wrapper",
      ".zoom-controls-fixed",
      ".canvas-height-editor",
      ".shape-properties-panel",
      "[data-canvas-chrome]",
    ].join(", "),
  );
};

/**
 * Create a function to handle native paste events
 * @param {Object} params - Parameters for native paste handling
 * @param {Function} params.setIsImageLoading - Setter for image loading state
 * @param {Function} params.setImageLoadingProgress - Setter for image loading progress
 * @param {Function} params.addImage - Function to add an image
 * @param {Function} params.showError - Function to show error snackbar (replaces alert)
 * @returns {Function} Function to handle native paste event
 */
export const createHandleNativePaste = ({
  setIsImageLoading,
  setImageLoadingProgress,
  addImage,
  showError,
}) => {
  return async (e) => {
    const target = e?.target;
    if (isTextInputOrContentEditable(target)) {
      // Allow default paste behavior for text inputs and contentEditable elements
      return;
    }

    const items = e.clipboardData?.items;
    if (
      items &&
      items.length > 0 &&
      (await tryPastePreferredRasterFromDataTransferItems(
        items,
        addImage,
        showError,
        setIsImageLoading,
        setImageLoadingProgress,
        e
      ))
    ) {
      return;
    }

    // When no image/png (etc.) on the clipboard, prefer HTTP URL from HTML (e.g. Google Images).
    const htmlFromPaste = e.clipboardData?.getData?.("text/html");
    if (htmlFromPaste) {
      const preferredUrl =
        extractBestHttpImageUrlFromClipboardHtml(htmlFromPaste);
      pasteFlowDebug("native paste: text/html present", {
        htmlLength: htmlFromPaste.length,
        preferredUrl: preferredUrl || null,
      });
      if (preferredUrl) {
        const urlValidation = validateImageUrl(preferredUrl);
        if (urlValidation.isValid) {
          e.preventDefault();
          pasteFlowDebug(
            "native paste: HTTP URL from HTML (no raster on clipboard — strip only for blobs)",
            preferredUrl.slice(0, 120)
          );
          setIsImageLoading(true);
          setImageLoadingProgress(10);
          addImage(
            preferredUrl,
            "",
            null,
            null,
            null,
            null,
            null,
            false,
            CLIPBOARD_PASTE_IMAGE_OPTS
          );
          return;
        }
      }
    }

    // Some browsers omit text/html but put the direct image URL in text/plain (e.g. "Copy image address").
    const plainPaste = e.clipboardData?.getData?.("text/plain")?.trim();
    if (
      plainPaste &&
      plainPaste.length < 4096 &&
      /^\s*https?:\/\/\S+$/.test(plainPaste)
    ) {
      const plainValidation = validateImageUrl(plainPaste);
      if (plainValidation.isValid) {
        e.preventDefault();
        pasteFlowDebug(
          "native paste: using text/plain URL (no blob strip)",
          plainPaste.slice(0, 120)
        );
        setIsImageLoading(true);
        setImageLoadingProgress(10);
        addImage(
          plainPaste,
          "",
          null,
          null,
          null,
          null,
          null,
          false,
          CLIPBOARD_PASTE_IMAGE_OPTS
        );
        return;
      }
    }

    if (!items) return;

    let hasImage = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const itemType = item.type;

      // Check if it's an image type
      if (itemType && itemType.indexOf("image") !== -1) {
        // CRITICAL: Check MIME type FIRST before reading the blob
        // This catches unsupported formats even if the OS converted the blob data.
        // If clipboard reports unsupported format (e.g., "image/tiff"), reject immediately
        // even if OS converted the blob bytes to a supported format.
        // Note: If OS converts both MIME type AND blob bytes, we cannot detect original format.
        const unsupportedInfo = getUnsupportedFormatInfo(itemType);
        if (unsupportedInfo) {
          setIsImageLoading(false);
          setImageLoadingProgress(0);
          const errorMessage = getUnsupportedFormatMessage(itemType);
          if (showError) {
            showError(errorMessage);
          } else {
            alert(errorMessage);
          }
          console.warn(
            "🚫 Native paste: Unsupported MIME type detected:",
            itemType,
            errorMessage
          );
          continue; // Skip this item
        }

        // Validate the image format
        const blob = item.getAsFile();
        if (!blob) continue;

        // Validate the image blob content using file-type detection
        // This validates the actual file contents, not just MIME type
        // This is async, so we can properly validate even in native paste events
        const validation = await validateImageBlobContent(
          blob,
          `pasted-image-${Date.now()}`
        );

        if (!validation.isValid) {
          setIsImageLoading(false);
          setImageLoadingProgress(0);
          if (showError) {
            showError(validation.error);
          } else {
            alert(validation.error);
          }
          continue;
        }

        if (!hasImage) {
          setIsImageLoading(true);
          setImageLoadingProgress(10);
          hasImage = true;
        }

        pasteFlowDebug("native paste: image/* blob path (stripCheckerboard applies in addImage)", {
          itemType,
          blobType: blob.type,
          blobSize: blob.size,
        });

        const src = URL.createObjectURL(blob);
        // Pass the original blob to addImage for better error handling
        addImage(
          src,
          "",
          blob,
          null,
          null,
          null,
          null,
          false,
          CLIPBOARD_PASTE_IMAGE_OPTS
        );
      }
    }
  };
};

/**
 * Create a function to handle paste operations
 * @param {Object} params - Parameters for paste handling
 * @param {Array} params.clipboard - Current clipboard array
 * @param {Function} params.handlePasteElements - Function to paste elements from clipboard
 * @param {Function} params.addImage - Function to add an image
 * @param {Function} params.showError - Function to show error snackbar (replaces alert)
 * @returns {Function} Function to handle paste operation
 */
export const createHandlePaste = ({
  clipboard,
  handlePasteElements,
  addImage,
  showError,
}) => {
  return async () => {
    // ✅ Step 1: Check internal clipboard first (from cut/copy operations within the app)
    if (clipboard.length > 0) {
      handlePasteElements();
      return;
    }

    // ✅ Step 2: Check if Clipboard API is available
    if (!navigator.clipboard || !navigator.clipboard.read) {
      const errorMessage =
        "Clipboard API is not available. Please use Ctrl+V (Cmd+V on Mac) to paste images.";
      if (showError) {
        showError(errorMessage);
      } else {
        alert(errorMessage);
      }
      return;
    }

    // ✅ Step 3: Try native clipboard API for external images
    const tryNativeClipboardAPI = async () => {
      try {
        // Request clipboard permissions if needed (some browsers require this)
        // Note: clipboard-read permission query may not be supported in all browsers
        if (navigator.permissions && navigator.permissions.query) {
          try {
            // Try clipboard-read permission (Chrome/Edge)
            const permission = await navigator.permissions
              .query({
                name: "clipboard-read",
              })
              .catch(() => null);

            if (permission && permission.state === "denied") {
              const errorMessage =
                "Clipboard access is denied. Please allow clipboard access in your browser settings, or use Ctrl+V (Cmd+V on Mac) to paste images.";
              if (showError) {
                showError(errorMessage);
              } else {
                alert(errorMessage);
              }
              return false;
            }
          } catch (permError) {
            // Permissions API might not be supported or have different syntax, continue anyway
          }
        }

        // Read clipboard items
        const clipboardItems = await navigator.clipboard.read();

        pasteFlowDebug("Clipboard.read() items", {
          count: clipboardItems.length,
          typesPerItem: clipboardItems.map((ci) => [...(ci.types || [])]),
        });

        if (
          await tryPastePreferredRasterFromClipboardItems(
            clipboardItems,
            addImage,
            showError
          )
        ) {
          return true;
        }

        const htmlFragments = [];
        for (const ci of clipboardItems) {
          const t = ci.types || [];
          if (!t.includes("text/html")) continue;
          try {
            const htmlBlob = await ci.getType("text/html");
            htmlFragments.push(await htmlBlob.text());
          } catch {
            // ignore
          }
        }
        const preferredFromHtml =
          extractBestHttpImageUrlFromClipboardHtmlFragments(htmlFragments);
        if (preferredFromHtml) {
          const urlValidation = validateImageUrl(preferredFromHtml);
          pasteFlowDebug("Clipboard API: HTML-derived URL (fallback — no usable raster blob)", {
            url: preferredFromHtml.slice(0, 120),
            valid: urlValidation.isValid,
          });
          if (urlValidation.isValid) {
            pasteFlowDebug(
              "Clipboard API: addImage from URL (strip not applied to remote file)"
            );
            addImage(
              preferredFromHtml,
              "",
              null,
              null,
              null,
              null,
              null,
              false,
              CLIPBOARD_PASTE_IMAGE_OPTS
            );
            return true;
          }
        }

        try {
          const plainClip = (await navigator.clipboard.readText()).trim();
          if (
            plainClip &&
            plainClip.length < 4096 &&
            /^\s*https?:\/\/\S+$/.test(plainClip)
          ) {
            const pv = validateImageUrl(plainClip);
            if (pv.isValid) {
              pasteFlowDebug(
                "Clipboard API: addImage from readText URL",
                plainClip.slice(0, 120)
              );
              addImage(
                plainClip,
                "",
                null,
                null,
                null,
                null,
                null,
                false,
                CLIPBOARD_PASTE_IMAGE_OPTS
              );
              return true;
            }
          }
        } catch {
          // readText may be unavailable or blocked; fall through to image/* handling
        }

        let imageFound = false;

        for (const clipboardItem of clipboardItems) {
          // Check all available types
          const types = clipboardItem.types || [];

          // Debug: Log all available clipboard types to see if we can get file metadata

          // CRITICAL: Check ALL reported MIME types FIRST before reading the blob
          // This catches unsupported formats even if the OS converted the blob data.
          //
          // Strategy:
          // 1. Check MIME types FIRST - if clipboard API reports unsupported format (e.g., "image/tiff"),
          //    reject immediately even if OS converted the blob bytes to a supported format.
          // 2. If MIME types are all supported, validate blob contents as secondary check.
          //
          // Note: If OS converts both the MIME type AND blob bytes (TIFF -> PNG), we cannot detect
          // the original format. This is a fundamental limitation of clipboard operations.
          // However, many OSes preserve the original MIME type in the clipboard, which we check here.
          let hasUnsupportedMimeType = false;
          let unsupportedMimeError = null;
          for (const type of types) {
            if (type.startsWith("image/")) {
              // Check if this MIME type is unsupported
              const unsupportedInfo = getUnsupportedFormatInfo(type);
              if (unsupportedInfo) {
                hasUnsupportedMimeType = true;
                unsupportedMimeError = getUnsupportedFormatMessage(type);
                console.warn(
                  "🚫 Clipboard reports unsupported MIME type:",
                  type,
                  unsupportedMimeError
                );
                break; // Stop checking, we found an unsupported type
              }
            }
          }

          // If clipboard reports any unsupported MIME type, reject immediately
          if (hasUnsupportedMimeType) {
            if (showError) {
              showError(unsupportedMimeError);
            } else {
              alert(unsupportedMimeError);
            }
            continue; // Skip this clipboard item
          }

          // Check if there's a file list type (some browsers might support this)
          // Note: File list is not standard in Clipboard API, but checking anyway
          let hasFileList = false;
          let fileList = null;
          try {
            // Some browsers might provide file information through different types
            for (const type of types) {
              if (type === "text/uri-list" || type === "text/x-moz-url") {
                // Might contain file paths
                const text = await clipboardItem
                  .getType(type)
                  .then((blob) => blob.text());
              }
            }
          } catch (e) {
            // Not available, continue
          }

          for (const type of types) {
            // Check for image types (image/png, image/jpeg, image/webp, image/svg+xml, image/x-icon, image/avif)
            if (type.startsWith("image/")) {
              try {
                const blob = await clipboardItem.getType(type);

                // CRITICAL: Validate image blob content by checking file contents first
                // This validates the actual file contents, not just MIME type
                // This ensures we catch unsupported formats like TIFF even if clipboard says "image/png"
                // IMPORTANT: When files are copied to clipboard, the OS may convert them (e.g., TIFF -> PNG)
                // We validate the actual file contents to detect the real format
                const validation = await validateImageBlobContent(
                  blob,
                  `pasted-image-${Date.now()}`
                );

                // Debug logging - check what we detected

                // Log first few bytes to help debug format detection
                try {
                  const sampleBytes = await blob.slice(0, 8).arrayBuffer();
                  const uint8Array = new Uint8Array(sampleBytes);
                } catch (e) {
                  console.warn("Could not read sample bytes:", e);
                }

                if (!validation.isValid) {
                  if (showError) {
                    showError(validation.error);
                  } else {
                    alert(validation.error);
                  }
                  continue;
                }

                // NOTE: If the OS converted an unsupported format (e.g., TIFF) to a supported format (e.g., PNG)
                // during clipboard copy, we cannot detect the original format. The image will be accepted.
                // For strict format validation of unsupported formats like TIFF, use the "Upload Image" button instead.
                console.info(
                  "ℹ️ Clipboard paste: Image accepted. Note: If your OS converted the image format during copy " +
                    "(e.g., TIFF → PNG), we cannot detect the original format. For strict format validation, " +
                    "please use the 'Upload Image' button for unsupported formats like TIFF."
                );

                pasteFlowDebug("Clipboard API: image/* blob", {
                  type,
                  blobType: blob.type,
                  blobSize: blob.size,
                });

                const src = URL.createObjectURL(blob);
                // Pass the original blob to addImage for better error handling
                addImage(
                  src,
                  "",
                  blob,
                  null,
                  null,
                  null,
                  null,
                  false,
                  CLIPBOARD_PASTE_IMAGE_OPTS
                );
                imageFound = true;
                return true; // success
              } catch (blobError) {
                console.error("Error reading blob from clipboard:", blobError);
                // Continue to next type
              }
            }
          }
        }

        if (!imageFound) {
          // Check if clipboard has HTML content with embedded images
          try {
            // Try to get HTML content which might contain images
            for (const clipboardItem of clipboardItems) {
              const types = clipboardItem.types || [];
              if (types.includes("text/html")) {
                try {
                  const htmlBlob = await clipboardItem.getType("text/html");
                  const htmlText = await htmlBlob.text();

                  // Try to extract image from HTML (img tag with src)
                  // Word often embeds images in HTML with base64 data URLs
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(htmlText, "text/html");

                  // Look for img tags
                  const imgElements = doc.querySelectorAll("img");

                  for (const imgElement of imgElements) {
                    if (imgElement && imgElement.src) {
                      const imgSrc = imgElement.src;

                      // Check if it's a data URL (most common for Word images)
                      if (imgSrc.startsWith("data:image/")) {
                        // Validate the data URL format
                        try {
                          // Extract MIME type from data URL
                          const mimeMatch = imgSrc.match(/data:([^;]+)/);
                          if (mimeMatch) {
                            const reportedMimeType = mimeMatch[1];
                            // Create a blob from base64 data
                            const base64Data = imgSrc.split(",")[1];
                            const byteString = atob(base64Data);
                            const ab = new ArrayBuffer(byteString.length);
                            const ia = new Uint8Array(ab);
                            for (let i = 0; i < byteString.length; i++) {
                              ia[i] = byteString.charCodeAt(i);
                            }
                            const blob = new Blob([ab], {
                              type: reportedMimeType,
                            });

                            // CRITICAL: Validate image blob content using file-type detection
                            // Word might embed TIFF but report it as PNG in the data URL
                            const validation = await validateImageBlobContent(
                              blob,
                              `word-image-${Date.now()}`
                            );

                            if (validation.isValid) {
                              addImage(
                                imgSrc,
                                "",
                                null,
                                null,
                                null,
                                null,
                                null,
                                false,
                                CLIPBOARD_PASTE_IMAGE_OPTS
                              );
                              return true;
                            } else {
                              // Show error for unsupported format and skip this image
                              if (showError) {
                                showError(validation.error);
                              } else {
                                alert(validation.error);
                              }
                              continue;
                            }
                          }
                        } catch (e) {
                          console.warn("Error validating image from HTML:", e);
                          continue;
                        }
                      } else if (
                        imgSrc.startsWith("http://") ||
                        imgSrc.startsWith("https://") ||
                        imgSrc.startsWith("blob:")
                      ) {
                        // Validate external URLs before adding
                        const urlValidation = validateImageUrl(imgSrc);
                        if (!urlValidation.isValid) {
                          if (showError) {
                            showError(urlValidation.error);
                          } else {
                            alert(urlValidation.error);
                          }
                          continue;
                        }
                        // For external URLs, validation will also happen on load if format can't be determined from URL
                        addImage(
                          imgSrc,
                          "",
                          null,
                          null,
                          null,
                          null,
                          null,
                          false,
                          CLIPBOARD_PASTE_IMAGE_OPTS
                        );
                        return true;
                      }
                    }
                  }
                } catch (htmlError) {
                }
              }
            }
          } catch (htmlReadError) {
          }

          // Check if clipboard has text (might be an image URL or data URL)
          try {
            const clipboardText = await navigator.clipboard.readText();
            if (clipboardText) {
              // Check if it's a data URL
              if (clipboardText.startsWith("data:image/")) {
                // Validate data URL format
                const dataUrlValidation = validateDataUrlImage(clipboardText);
                if (!dataUrlValidation.isValid) {
                  if (showError) {
                    showError(dataUrlValidation.error);
                  } else {
                    alert(dataUrlValidation.error);
                  }
                  return false;
                }
                addImage(
                  clipboardText,
                  "",
                  null,
                  null,
                  null,
                  null,
                  null,
                  false,
                  CLIPBOARD_PASTE_IMAGE_OPTS
                );
                return true;
              }
              // Check if it's an image URL (by extension pattern)
              else if (
                clipboardText.match(/\.(jpg|jpeg|png|webp|svg|ico|avif)$/i)
              ) {
                // Validate URL pattern
                const urlValidation = validateImageUrl(clipboardText);
                if (!urlValidation.isValid) {
                  if (showError) {
                    showError(urlValidation.error);
                  } else {
                    alert(urlValidation.error);
                  }
                  return false;
                }
                addImage(
                  clipboardText,
                  "",
                  null,
                  null,
                  null,
                  null,
                  null,
                  false,
                  CLIPBOARD_PASTE_IMAGE_OPTS
                );
                return true;
              }
              // Check if it's an HTTP/HTTPS URL (might be an image without extension)
              else if (
                clipboardText.startsWith("http://") ||
                clipboardText.startsWith("https://")
              ) {
                // Validate URL pattern (will allow if no extension, validation happens on load)
                const urlValidation = validateImageUrl(clipboardText);
                if (!urlValidation.isValid) {
                  if (showError) {
                    showError(urlValidation.error);
                  } else {
                    alert(urlValidation.error);
                  }
                  return false;
                }
                addImage(
                  clipboardText,
                  "",
                  null,
                  null,
                  null,
                  null,
                  null,
                  false,
                  CLIPBOARD_PASTE_IMAGE_OPTS
                );
                return true;
              }
            }
          } catch (textError) {
            // Text read failed, continue
          }

          // Silently return false when no image is found (e.g., when pasting text)
          // No alert needed as this is expected behavior when pasting non-image content
        }

        return imageFound;
      } catch (error) {
        console.error("Clipboard read error:", error);

        // Provide more specific error messages
        if (error.name === "NotAllowedError") {
          const errorMessage =
            "Clipboard access denied. Please allow clipboard permissions and try again, or use Ctrl+V (Cmd+V on Mac) to paste.";
          if (showError) {
            showError(errorMessage);
          } else {
            alert(errorMessage);
          }
        } else if (error.name === "NotFoundError") {
          const errorMessage = "No data found in clipboard.";
          if (showError) {
            showError(errorMessage);
          } else {
            alert(errorMessage);
          }
        } else if (error.name === "SecurityError") {
          const errorMessage =
            "Clipboard access blocked for security reasons. Please use Ctrl+V (Cmd+V on Mac) to paste images.";
          if (showError) {
            showError(errorMessage);
          } else {
            alert(errorMessage);
          }
        } else {
          // Generic error - try fallback
          console.error("Unexpected clipboard error:", error);
          return false;
        }
        return false;
      }
    };

    const success = await tryNativeClipboardAPI();

    // ✅ Step 4: Fallback if failed - try to use paste event simulation
    if (!success) {
      // Focus a hidden input to potentially catch native paste
      const fallbackInput = document.getElementById("clipboard-fallback");
      if (fallbackInput) {
        fallbackInput.focus();
        // Try to trigger paste programmatically (may not work in all browsers)
        try {
          fallbackInput.select();
          document.execCommand("paste");
        } catch (execError) {
          // execCommand might not work, that's okay
        }
      }
    }
  };
};
